/**
 * Scope keys, mirroring the engineer-curated catalog seeded in
 * clockout-service/sql/V4__rbac_roles_scopes.sql. These are the only scope
 * strings any frontend code should reference by name — everything else goes
 * through the dynamic catalog fetched for the Role Composer.
 */
export const SCOPES = {
    DASHBOARD_VIEW: 'screen:dashboard:view',
    TREASURY_VIEW: 'screen:treasury:view',
    CONFIGURATIONS_VIEW: 'screen:configurations:view',
    CONFIGURATIONS_MANAGE: 'screen:configurations:manage',
    // Governs writes AND reads on the client access list (whitelist/
    // blacklist member_id overrides), single and bulk. Kept distinct from
    // CONFIGURATIONS_MANAGE: an ALLOW rule bypasses every other risk and
    // segmentation check, so an FI should be able to delegate the rest of
    // Configurations without delegating this. Checked in addition to
    // CONFIGURATIONS_VIEW, never instead of it.
    ACCESS_LIST_MANAGE: 'admin:access_list:manage',
    BILLING_VIEW: 'screen:billing:view',
    TEAM_MANAGE: 'screen:team:manage',
    REPORTS_VIEW: 'screen:reports:view',
    REPORTS_TRANSACTIONS_VIEW: 'screen:reports:transactions:view',
    REVIEWS_VIEW: 'screen:reviews:view',
    SUPPORT_VIEW: 'screen:support:view',
    ROLES_VIEW: 'screen:roles:view',
    ROLES_MANAGE: 'admin:roles:manage',
    TEAM_VIEW: 'screen:team:view',
    TEAM_INVITE: 'admin:team:invite',
    ASSIGN_OWNER_ROLE: 'admin:team:assign_owner_role',
    // Per-action scopes for the destructive Team row actions. Split out of
    // admin:roles:manage (the Role Composer scope, which is what used to
    // guard them server-side) so an FI can hand out member management
    // without handing out the role catalog. Checked in addition to
    // TEAM_VIEW, never instead of it.
    TEAM_REMOVE_ACCESS: 'admin:team:remove_access',
    TEAM_RESET_PASSWORD: 'admin:team:reset_password',
    TEAM_CHANGE_ROLE: 'admin:team:change_role',
    UPLOADS_SIMULATOR_RUN: 'admin:uploads_simulator:run',
    OPENSSO_ENABLED: 'feature:opensso:enabled',
    DDA_SWITCH_VISIBLE: 'feature:dda_switch:visible',
    // Feature gate (not an authorization boundary) for member-specific
    // free-advance promotion groups. Shipped OFF everywhere by default; an
    // FI's Owner is granted this automatically the moment the scope exists
    // (see clockout-service sql/V12__free_advance_promo_scope.sql), but it
    // only appears here — and in the Role Composer catalog — once an
    // operator flips its `enabled` column on for that FI's instance.
    FREE_ADVANCE_PROMOS: 'feature:free_advance_promos:enabled',
    // Per-dimension detail visibility in the Reports > Users sync status
    // table — gates only the tooltip's detail rows (client-side), never the
    // icon/status itself. The API always returns the full report; masking
    // happens here, not on the backend.
    IDENTITY_VIEW: 'admin:identity:view',
    EMPLOYMENT_VIEW: 'admin:employment:view',
    INCOME_VIEW: 'admin:income:view',
    DIRECT_DEPOSIT_VIEW: 'admin:direct_deposit:view',
    // Impersonate a specific user via SSO from Reports > Users or /users/:id
    // — distinct from OPENSSO_ENABLED, which gates the standalone /open-sso
    // page as a broad feature flag. Delegable, unlike USERS_RECONCILE below:
    // who may enter a member's session is the FI's call, so an owner can put
    // it on Admin or any custom role from the Role Composer.
    SSO_OPEN: 'admin:sso:open',
    // Settle a user's entire outstanding debt with one aggregate ledger
    // adjustment, from /users/:id. Flagged visible_owner_only in the catalog,
    // so the API hides it from non-owners' Role Composer and refuses to
    // assign it to any role but the FI's owner role.
    USERS_RECONCILE: 'admin:users:reconcile',
    // Restrict or restore a member's platform access (toggles account.active),
    // from the Reports > Users row toolbar, the detail panel, and /users/:id.
    // Split out of TEAM_INVITE (V15): that scope is about inviting staff to
    // this console, and gating member lockout on it meant an FI could not
    // delegate one without the other. Owner + Admin by default, and unlike
    // USERS_RECONCILE it is delegable to a custom role.
    USERS_RESTRICT: 'admin:users:restrict',
    // The two payroll-sync row actions, from the Reports > Users toolbar and
    // the sync-status card on /users/:id. Also split out of TEAM_INVITE (V15).
    // Separate keys because deleting the latest sync row cannot be undone,
    // while marking it complete only unsticks a member's loading state.
    SYNC_DELETE_LATEST: 'admin:sync:delete_latest',
    SYNC_MARK_COMPLETE: 'admin:sync:mark_complete',
    // Expand a dimension of the Payroll Sync Status card on /users/:id to the
    // data the active sync stored (identity, employment, income, paystubs,
    // direct deposit, shifts). Unlike the per-dimension view scopes above this
    // one is enforced server-side too: without it the sync-history endpoint
    // returns statuses only. Owner only by default (V18), delegable.
    SYNC_VIEW_STORED_DATA: 'admin:sync:view_stored_data',
    // The Jira tickets behind this FI's support requests: the "Jira Tickets"
    // tab on Support, and the linked-issues card on a ticket's detail.
    //
    // Checked in ADDITION to SUPPORT_VIEW, like the per-tab scopes below — but
    // unlike those it is not a `screen:` key, because the same scope also gates
    // the server side (`/api/admin/jira/*`, where it REPLACED
    // `screen:support:view`) and the Clockout staff panel's linking routes.
    // V17 backfilled it onto every role that already held SUPPORT_VIEW, so no
    // role's view changed the day it landed; it is simply revocable now.
    //
    // An FI may put it on any role, including a custom one — it is not
    // visible_owner_only.
    JIRA_TICKETS_VIEW: 'admin:jira:tickets:view',
    // Per-tab visibility within Dashboard / Cashflow Management. Checked in
    // addition to DASHBOARD_VIEW / TREASURY_VIEW, never instead of them.
    DASHBOARD_USAGE_VIEW: 'screen:dashboard:usage:view',
    DASHBOARD_ENROLLMENT_VIEW: 'screen:dashboard:enrollment:view',
    DASHBOARD_MONETIZATION_VIEW: 'screen:dashboard:monetization:view',
    TREASURY_ADVANCE_VIEW: 'screen:treasury:advance:view',
    TREASURY_FEE_VIEW: 'screen:treasury:fee:view',
};

/**
 * Synchronous scope check for plain (non-hook) reads, e.g. filtering the
 * sidebar's navbarSections array inside a class component's render(), where
 * `useHasScope()` can't be called. Reads the same `user.scopes` array the
 * `useScope` hooks read reactively elsewhere.
 * @param {{ scopes?: string[] } | null | undefined} user
 * @param {string | null | undefined} scope
 * @returns {boolean}
 */
export function userHasScope(user, scope) {
    if (!scope) return true;
    return (user?.scopes || []).includes(scope);
}
